import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Flex,
  Spinner,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import * as React from "react";
import { useHistory } from "react-router-dom";
import createPersistedState from "use-persisted-state";
import { api } from "../../config";
import { useQuery } from "@tanstack/react-query";

const columns = [
  {
    id: "groupName",
    name: "Group Name",
  },
  {
    id: "fee",
    name: "Fee",
  },
];

const ViewGroups: React.FC = () => {
  const useCounterState = createPersistedState("sortQueryGroup");
  const [sortQueryGroup, setSortQueryGroup]: any =
    useCounterState("groupName:asc");

  const history = useHistory();
  const toast = useToast();

  // New state for search & pagination
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  // Fetch function with search, pagination, and sorting params
  const fetchGroupsAndCount = async (
    page: number,
    pageSize: number,
    sort: string,
    groupName: string
  ) => {
    try {
      const { data: groups } = await axios.get(
        `${api}/groups?_limit=${pageSize}&_start=${
          page * pageSize
        }&_sort=${sort}&groupName_contains=${groupName || ""}`,
        { withCredentials: true }
      );
      const { data: totalCount } = await axios.get(`${api}/groups/count`, {
        withCredentials: true,
      });
      return { groups, totalCount };
    } catch (error: any) {
      toast({
        title: error.message,
        position: "bottom-right",
        status: "error",
      });
      return { groups: [], totalCount: 0 };
    }
  };

  const { data, isLoading, error, refetch } = useQuery(
    ["groups", page, pageSize, sortQueryGroup, search],
    () => fetchGroupsAndCount(page, pageSize, sortQueryGroup, search),
    { keepPreviousData: true }
  );

  const handleEditClick = (id: string) => {
    history.push(`/admin/group-form/${id}`);
  };
  const handleManageClick = (id: string) => {
    history.push(`/admin/group-class-form/${id}`);
  };
  const handleAddClick = () => {
    history.push("/admin/group-form");
  };

  return (
    <>
      <Button
        colorScheme="blue"
        onClick={handleAddClick}
        className="add-btn"
        m="3"
        size="sm"
        leftIcon={<AddIcon />}
        textTransform="uppercase">
        Add Class
      </Button>

      {/* Search input */}
      <Box px="3" mb="3" maxW="400px">
        <Input
          placeholder="Search Group Name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0); // reset page when search changes
          }}
        />
      </Box>

      <Box overflowX="auto" px="2">
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <Text color="red.500">Error loading groups.</Text>
        ) : (
          <Table variant="simple" size="md">
            <Thead>
              <Tr>
                <Th>Class Name</Th>
                <Th>Fee</Th>
                <Th>Inactive</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data?.groups.map((item: any) => (
                <Tr key={item.id}>
                  <Td>{item.groupName}</Td>
                  <Td>{item.fee}</Td>
                  <Td>
                    {item.inactive ? (
                      <Box color="red.500">Inactive</Box>
                    ) : (
                      <Box color="green.500">Active</Box>
                    )}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      mr="2"
                      onClick={() => handleManageClick(item.id)}>
                      Manage
                    </Button>
                    <Button size="sm" onClick={() => handleEditClick(item.id)}>
                      Edit
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Pagination controls */}
      <Flex justify="space-between" align="center" px="3" mt="3">
        <Button
          onClick={() => setPage((old) => Math.max(old - 1, 0))}
          disabled={page === 0}
          size="sm">
          Previous
        </Button>
        <Box>
          Page {page + 1} of{" "}
          {Math.ceil((data?.totalCount ?? 0) / pageSize) || 1}
        </Box>
        <Button
          onClick={() =>
            setPage((old) =>
              !data || !data.totalCount
                ? old
                : old + 1 < Math.ceil(data.totalCount / pageSize)
                ? old + 1
                : old
            )
          }
          disabled={
            !data || page + 1 >= Math.ceil((data?.totalCount ?? 0) / pageSize)
          }
          size="sm">
          Next
        </Button>
      </Flex>
    </>
  );
};

export default ViewGroups;
